import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Card, 
  CardContent, 
  LinearProgress, 
  Paper,
  Tabs,
  Tab
} from '@mui/material';
import { Link } from 'react-router-dom';

// Icons
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import BiotechOutlinedIcon from '@mui/icons-material/BiotechOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

/**
 * AI Assistant component that displays AI-generated insights about a document
 * @param {Object} props
 * @param {boolean} props.isCollapsed - Whether the assistant panel is collapsed
 * @param {function} props.onToggleCollapse - Function to toggle collapse state
 */
const AIAssistant = ({ isCollapsed, onToggleCollapse }) => {
  // Add animation state to control transitions
  // Default to 'expanded' if isCollapsed is not provided (i.e., collapse feature disabled)
  const [animationState, setAnimationState] = useState(typeof isCollapsed === 'undefined' ? 'expanded' : (isCollapsed ? 'collapsed' : 'expanded'));
  
  // Update animation state when isCollapsed changes, only if the prop is provided
  useEffect(() => {
    if (typeof isCollapsed !== 'undefined') {
      setAnimationState(isCollapsed ? 'collapsing' : 'expanding');
      
      // After animation completes, update to final state
      const timer = setTimeout(() => {
        setAnimationState(isCollapsed ? 'collapsed' : 'expanded');
      }, 300); // Match this with the transition duration
      
      return () => clearTimeout(timer);
    }
  }, [isCollapsed]);

  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  return (
    <>
      {/* Always render the content, but control visibility with CSS */}
      <Paper 
        elevation={2} 
        sx={{ 
          pt: 0, pl: 1.5, pr: 1.5, pb: 1.5, // Adjusted padding to remove top space
          height: 'auto', 
          minHeight: 'calc(100vh - 150px)',
          maxHeight: '350px',
          overflowY: 'auto', 
          position: 'relative',
          width: '100%',
          transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          transform: (typeof isCollapsed !== 'undefined' && (animationState === 'collapsed' || animationState === 'collapsing')) ? 'translateX(100%)' : 'translateX(0)',
          opacity: (typeof isCollapsed !== 'undefined' && animationState === 'collapsed') ? 0 : 1,
          visibility: (typeof isCollapsed !== 'undefined' && animationState === 'collapsed') ? 'hidden' : 'visible',
          // Use will-change to optimize the animation
          willChange: 'transform, opacity',
          display: 'flex',
          flexDirection: 'column',
          // Use hardware acceleration for smoother animations
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden'
        }}
      >
          {onToggleCollapse && (
            <IconButton 
              onClick={onToggleCollapse}
              sx={{ 
                position: 'absolute', 
                top: 8, 
                left: 8, 
                zIndex: 1, 
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                }
              }}
              size="small"
            >
              {isCollapsed ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          )}

          <Tabs value={activeTab} onChange={handleTabChange} aria-label="AI Assistant sections" variant="fullWidth" indicatorColor="success" TabIndicatorProps={{ sx: { backgroundColor: 'success.main' } }} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab icon={<PsychologyOutlinedIcon />} iconPosition="start" label="Assistant" sx={{minWidth: 'auto', flexGrow: 1, fontSize: '0.8rem', textTransform: 'none', minHeight: '32px', paddingTop: '4px', paddingBottom: '4px', paddingLeft: '8px', paddingRight: '8px', '.MuiTab-iconWrapper': { marginRight: '4px' }, '&.Mui-selected': { color: 'success.main' }, color: 'text.secondary' }} />
            <Tab icon={<TuneIcon />} iconPosition="start" label="Properties" sx={{minWidth: 'auto', flexGrow: 1, fontSize: '0.8rem', textTransform: 'none', minHeight: '32px', paddingTop: '4px', paddingBottom: '4px', paddingLeft: '8px', paddingRight: '8px', '.MuiTab-iconWrapper': { marginRight: '4px' }, '&.Mui-selected': { color: 'success.main' }, color: 'text.secondary' }} />
            <Tab icon={<ChatBubbleOutlineIcon />} iconPosition="start" label="Discussion" sx={{minWidth: 'auto', flexGrow: 1, fontSize: '0.8rem', textTransform: 'none', minHeight: '32px', paddingTop: '4px', paddingBottom: '4px', paddingLeft: '8px', paddingRight: '8px', '.MuiTab-iconWrapper': { marginRight: '4px' }, '&.Mui-selected': { color: 'success.main' }, color: 'text.secondary' }} />
          </Tabs>

          {activeTab === 0 && (
            <Box sx={{pt: 1.5, flexGrow: 1, overflowY: 'auto'}}> {/* Add padding top and allow this Box to scroll if content overflows */}
          {/* Missing Predicate Device - Pale blue background section */}
          <Card variant="outlined" sx={{ mb: 1.5, bgcolor: '#e8f4fd', color: '#0a3977' }}>
            <CardContent sx={{ padding: '12px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ color: '#0a3977', display: 'flex', alignItems: 'center' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5.99L19.53 19H4.47L12 5.99ZM12 2L1 21H23L12 2ZM13 16H11V18H13V16ZM13 10H11V14H13V10Z" fill="currentColor"/>
                  </svg>
                </Box>
                <Typography variant="subtitle2" fontWeight="bold">Missing Predicate Device</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                I notice you haven't identified a predicate device yet.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                  component={Link}
                  to="/Keywords"
                  sx={{
                    bgcolor: '#f0f0f0',
                    color: '#000000',
                    border: '1px solid #cccccc',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    fontSize: '0.8125rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '220px',
                    textDecoration: 'none',
                    '&:hover': { bgcolor: '#e6e6e6' }
                  }}
                >
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="currentColor"/>
                    </svg>
                  </Box>
                  Search Similar Devices
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ padding: '12px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PsychologyOutlinedIcon color="action" />
                <Typography variant="subtitle2" fontWeight="bold">Regulatory Classification</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Likely Class II medical device based on intended use (monitoring vital signs). Requires 510(k) premarket notification.
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">Confidence:</Typography>
              <LinearProgress variant="determinate" value={95} sx={{ height: 8, borderRadius: 4 }}/>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ textAlign: 'right' }}>95%</Typography>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ padding: '12px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WarningAmberOutlinedIcon color="warning" />
                <Typography variant="subtitle2" fontWeight="bold">Potential Risks Identified</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                - Inaccurate readings due to sensor placement.
                - Data security risks with wireless transmission.
                - Battery failure leading to monitoring interruption.
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">Risk Level:</Typography>
              <LinearProgress variant="determinate" value={65} color="warning" sx={{ height: 8, borderRadius: 4 }}/>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ textAlign: 'right' }}>Moderate (65%)</Typography>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ padding: '12px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SearchOutlinedIcon color="action" />
                <Typography variant="subtitle2" fontWeight="bold">Consider TempGuard Pro as Predicate</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Temp Guard Pro (K123456) has similar intended use and technological characteristics. Recommend detailed comparison.
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">Confidence:</Typography>
              <LinearProgress variant="determinate" value={92} sx={{ height: 8, borderRadius: 4 }}/>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ textAlign: 'right' }}>92%</Typography>
            </CardContent>
          </Card>

          {/* Additional Performance Testing */}
          <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ padding: '12px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BiotechOutlinedIcon color="action" />
                <Typography variant="subtitle2" fontWeight="bold">Additional Performance Testing</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Recommend conducting EMC testing for wireless features and battery safety testing per IEC 60601-1.
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">Confidence:</Typography>
              <LinearProgress variant="determinate" value={90} sx={{ height: 8, borderRadius: 4 }} />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ textAlign: 'right' }}>90%</Typography>
            </CardContent>
          </Card>

          {/* Market Orientation */}
          <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ padding: '12px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUpOutlinedIcon color="action" />
                <Typography variant="subtitle2" fontWeight="bold">Market Orientation</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                While not necessarily a differentiator, it appears to be following the trend of adding wireless connectivity to existing devices.
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">Confidence:</Typography>
              <LinearProgress variant="determinate" value={80} sx={{ height: 8, borderRadius: 4 }} />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ textAlign: 'right' }}>80%</Typography>
            </CardContent>
          </Card>

          {/* Clinical Validation Needed */}
          <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ padding: '12px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ErrorOutlineOutlinedIcon color="error" />
                <Typography variant="subtitle2" fontWeight="bold">Clinical Validation Needed</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Recommend usability testing to validate UI improvements and wireless features.
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">Confidence:</Typography>
              <LinearProgress variant="determinate" value={30} sx={{ height: 8, borderRadius: 4 }} />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ textAlign: 'right' }}>30%</Typography>
            </CardContent>
          </Card>
          {/* End of Assistant Tab Content */}
          </Box>
          )}
          {activeTab === 1 && (
            <Box sx={{ p: 2, textAlign: 'center', flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography>Properties Content Placeholder</Typography>
            </Box>
          )}
          {activeTab === 2 && (
            <Box sx={{ p: 2, textAlign: 'center', flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography>Discussion Content Placeholder</Typography>
            </Box>
          )}
        </Paper>

      {/* Button to re-open assistant - rendered only if collapse functionality is enabled */}
      {onToggleCollapse && (
        <Paper
          elevation={3}
          sx={{ 
            position: 'fixed', 
            top: '50%', 
            right: 0, 
            transform: 'translateY(-50%)', 
            zIndex: 1300, 
            backgroundColor: 'white',
            color: 'text.primary',
            borderTopLeftRadius: '12px', 
            borderBottomLeftRadius: '12px',
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            // Transition for smooth animation
            transition: 'opacity 300ms ease',
            opacity: isCollapsed ? 1 : 0,
            visibility: isCollapsed ? 'visible' : 'hidden',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': {
              boxShadow: (theme) => theme.shadows[6],
            },
          }}
          onClick={onToggleCollapse}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', p: 1, pl: 1.5, pr: 2 }}>
            <PsychologyOutlinedIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle1">AI Assistant</Typography>
            <ChevronLeftIcon sx={{ ml: 1 }} />
          </Box>
        </Paper>
      )}
    </>
  );
};

export default AIAssistant;
